/*
 * Ryedical purchase selector (snippets/ryedical-offer.liquid).
 *
 * One controller for every surface that sells the pouch. It only keeps the
 * hidden form inputs, the selected styling and the summary bar in step;
 * Savor's <product-form-component> does the actual add to cart.
 *
 * Rules: 1 and 2 pouches are one-time only. 3 pouches is one-off or a
 * subscription. Choosing a plan selects 3 pouches; choosing 3 pouches
 * without a plan defaults to one-off.
 */
class RyedicalOffer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.supplies = [...this.querySelectorAll('[data-offer-supply]')];
    this.plans = [...this.querySelectorAll('[data-offer-plan]')];
    this.variantInput = this.querySelector('[data-offer-variant]');
    this.sellingPlanInput = this.querySelector('[data-offer-selling-plan]');
    this.submitButton = this.querySelector('[data-offer-submit]');
    this.submitLabel = this.querySelector('[data-offer-submit-label]');
    this.summaryLabel = this.querySelector('[data-offer-summary-label]');
    this.summaryPrice = this.querySelector('[data-offer-summary-price]');
    this.summaryShip = this.querySelector('[data-offer-summary-ship]');
    this.threePrice = this.querySelector('[data-offer-three-price]');
    this.threeNote = this.querySelector('[data-offer-three-note]');
    this.nudge = this.querySelector('[data-offer-nudge]');
    this.inCart = this.querySelector('[data-offer-incart]');
    this.inCartText = this.querySelector('[data-offer-incart-text]');
    this.cartItems = [];

    this.supplies.forEach((input) => input.addEventListener('change', () => this.update()));
    this.plans.forEach((input) => input.addEventListener('change', () => {
      const three = this.supplies.find((s) => s.value === '3');
      if (three && !three.disabled) three.checked = true;
      this.update();
    }));
    this.nudge?.addEventListener('click', () => {
      const three = this.supplies.find((s) => s.value === '3');
      if (!three || three.disabled) return;
      three.checked = true;
      this.update();
      three.focus({ preventScroll: true });
    });

    this.applyLinkedSupply();
    this.update();
    this.setupInfoDialog();

    // Shoppers who add, switch option and add again end up with duplicates,
    // so say when the current choice is already in the cart.
    this.refreshCart();
    document.addEventListener('shopify:cart:lines-update', this.handleCartLinesUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.handleCartLinesUpdate);
  }

  handleCartLinesUpdate = (event) => {
    Promise.resolve(event.promise).finally(() => this.refreshCart());
  };

  async refreshCart() {
    if (!this.inCart) return;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.cartItems = cart.items || [];
    } catch {
      this.cartItems = [];
    }
    this.updateInCart();
  }

  updateInCart() {
    if (!this.inCart) return;
    const variantId = Number(this.variantInput.value);
    const planId = this.sellingPlanInput.disabled ? null : Number(this.sellingPlanInput.value);
    const pouchLines = this.cartItems.filter((item) => String(item.product_id) === this.dataset.productId);
    const isSelected = (item) => item.variant_id === variantId && this.#planOf(item) === planId;
    const quantity = pouchLines.filter(isSelected).reduce((sum, item) => sum + item.quantity, 0);
    const others = pouchLines.filter((item) => !isSelected(item));

    // A different option is already in the cart: offer to switch instead of
    // adding a second line. The plain "already in your cart" note is only
    // for when the cart holds exactly the selected option.
    this.inCart.hidden = quantity === 0 || others.length > 0;
    if (quantity > 0 && others.length === 0) {
      const what = this.selectedSupply?.dataset.label || 'This option';
      const how = planId ? ' on subscription' : '';
      this.inCartText.textContent = quantity > 1
        ? `${what}${how} is already in your cart (×${quantity})`
        : `${what}${how} is already in your cart`;
    }
    this.renderSwitch(others);
  }

  #planOf(item) {
    return item.selling_plan_allocation?.selling_plan?.id ?? null;
  }

  /* "You have 2 pouches in your cart" + one button per option to switch to.
     On 3 pouches the shopper chooses one-time or subscription; 1 and 2
     pouches are one-time only. Each button shows the per-pouch saving. */
  renderSwitch(others) {
    const box = this.querySelector('[data-offer-switch]');
    if (!box) return;
    const supply = this.selectedSupply;
    box.hidden = !others.length || !supply || supply.disabled;
    // The "Get 3 pouches" nudge would compete with the switch buttons.
    const three = this.supplies.find((s) => s.value === '3');
    if (this.nudge) this.nudge.hidden = !box.hidden || supply?.value === '3' || !three || three.disabled;
    if (box.hidden) return;

    const labelFor = (item) => {
      const match = this.supplies.find((s) => Number(s.dataset.variantId) === item.variant_id);
      const label = match?.dataset.label || item.variant_title || 'Ryedical';
      const plan = this.#planOf(item) ? ' on subscription' : '';
      return `${label}${plan}${item.quantity > 1 ? ` (×${item.quantity})` : ''}`;
    };
    const held = others.map(labelFor);
    const list = held.length > 1 ? `${held.slice(0, -1).join(', ')} and ${held.at(-1)}` : held[0];
    box.querySelector('[data-offer-switch-text]').textContent = `You have ${list} in your cart.`;

    const save = (value) => (Number(value) > 0 ? ` · save ${value}% per pouch` : '');
    const freeShip = (input) => (/^free shipping$/i.test(input.dataset.shipping || '') ? ' + free shipping' : '');
    const targets = [];
    if (supply.value === '3' && this.plans.length) {
      this.plans.forEach((plan) => {
        const sub = plan.value === 'subscription';
        targets.push({
          plan,
          label: sub
            ? `Switch to 3-pouch subscription · ${plan.dataset.total}/month${save(plan.dataset.save)}${freeShip(plan)}`
            : `Switch to 3 pouches · one-time · ${plan.dataset.total}${save(plan.dataset.save)}${freeShip(plan)}`
        });
      });
    } else {
      targets.push({ plan: null, label: `Switch to ${supply.dataset.label} · ${supply.dataset.total}${save(supply.dataset.save)}${freeShip(supply)}` });
    }

    // Every pouch line goes, including one that matches the target, so the
    // cart ends with exactly one line. A target the cart already holds as its
    // only line would change nothing, so it is not offered.
    const pouchLines = this.cartItems.filter((item) => String(item.product_id) === this.dataset.productId);
    const lineKeys = pouchLines.map((item) => item.key);
    const only = pouchLines.length === 1 && pouchLines[0].quantity === 1 ? pouchLines[0] : null;
    const offered = targets.filter(({ plan }) => {
      if (!only || only.variant_id !== Number(supply.dataset.variantId)) return true;
      const planId = plan?.value === 'subscription' ? Number(plan.dataset.sellingPlanId) : null;
      return this.#planOf(only) !== planId;
    });
    targets.length = 0;
    targets.push(...offered);
    const actions = box.querySelector('[data-offer-switch-actions]');
    actions.replaceChildren(...targets.map(({ plan, label }) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ry-offer__switch-button';
      button.textContent = label;
      button.addEventListener('click', () => this.switchTo(supply, plan, lineKeys));
      return button;
    }));
  }

  /* Empties the pouch lines, then adds the chosen option through the normal
     add-to-cart button, so the cart count, drawer and loading state behave
     exactly as for any other add. */
  async switchTo(supply, plan, lineKeys) {
    const buttons = [...this.querySelectorAll('.ry-offer__switch-button')];
    buttons.forEach((b) => { b.disabled = true; });
    try {
      const updates = Object.fromEntries(lineKeys.map((key) => [key, 0]));
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Could not update the cart');
      this.cartItems = [];
      supply.checked = true;
      if (plan) plan.checked = true;
      this.update();
      this.submitButton.click();
    } catch (error) {
      console.error('[ryedical-offer] Switch failed:', error);
      buttons.forEach((b) => { b.disabled = false; });
    }
  }

  /* Links from the Science articles and the Compare page arrive with
     ?supply=3 so the 3-pouch option is preselected. PDP selector only
     (id="purchase-options"); the cart drawer and campaign pages keep their
     own default. */
  applyLinkedSupply() {
    if (this.id !== 'purchase-options') return;
    const wanted = new URLSearchParams(window.location.search).get('supply');
    const match = this.supplies.find((s) => s.value === wanted && !s.disabled);
    if (match) match.checked = true;
  }

  /* "Why two pouches" pop-up. The (i) sits inside the 2-pouch <label>, so the
     click is stopped from also selecting that row. */
  setupInfoDialog() {
    const dialog = this.querySelector('[data-offer-info-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    this.querySelectorAll('[data-offer-info-open]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.infoOpener = button;
        dialog.showModal();
      });
    });
    dialog.querySelector('[data-offer-info-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();   // click on the backdrop
    });
    dialog.addEventListener('close', () => this.infoOpener?.focus({ preventScroll: true }));
  }

  get selectedSupply() {
    return this.supplies.find((s) => s.checked && !s.disabled) || this.supplies.find((s) => !s.disabled);
  }

  update() {
    const supply = this.selectedSupply;
    if (!supply) return;
    if (!supply.checked) supply.checked = true;

    const isThree = supply.value === '3';
    if (isThree && this.plans.length && !this.plans.some((p) => p.checked)) this.plans[0].checked = true;
    if (!isThree) this.plans.forEach((p) => { p.checked = false; });

    const plan = isThree ? this.plans.find((p) => p.checked) : null;
    const isSubscription = plan?.value === 'subscription';

    this.querySelectorAll('[data-offer-option]').forEach((option) => {
      option.classList.toggle('is-selected', option.dataset.offerOption === supply.value);
    });
    this.plans.forEach((p) => p.closest('.ry-offer__plan')?.classList.toggle('is-selected', p.checked));

    this.variantInput.value = supply.dataset.variantId;
    this.sellingPlanInput.disabled = !isSubscription;
    this.sellingPlanInput.value = isSubscription ? plan.dataset.sellingPlanId : '';

    const total = isSubscription ? plan.dataset.total : supply.dataset.total;
    const shipping = isSubscription ? plan.dataset.shipping : supply.dataset.shipping;
    const methodTitle = isSubscription ? 'Subscribe & save' : 'One-time';

    const three = this.supplies.find((s) => s.value === '3');
    if (this.threePrice && isThree) {
      this.threePrice.textContent = total;
      this.threeNote.textContent = isSubscription ? `a month · ${shipping.toLowerCase()}` : shipping;
    } else if (this.threePrice && three) {
      this.threePrice.textContent = three.dataset.total;
      if (this.threeNote && !three.disabled) this.threeNote.textContent = three.dataset.shipping;
    }

    if (this.nudge) this.nudge.hidden = isThree || !three || three.disabled;

    this.summaryLabel.replaceChildren(`${supply.dataset.label} `, this.#dot(), ` ${methodTitle}`);
    this.summaryPrice.textContent = isSubscription ? `${total} a month` : total;
    if (this.summaryShip) this.summaryShip.textContent = shipping || '';
    this.submitLabel.textContent = isSubscription
      ? 'Add 3-pouch subscription to cart'
      : `Add ${supply.dataset.label} to cart`;
    this.submitButton.disabled = supply.disabled;
    this.updateInCart();

    // Read by the PDP sticky add-to-cart bar and the early-offer media swap.
    this.dispatchEvent(new CustomEvent('ryedical:purchase-change', {
      bubbles: true,
      detail: {
        supply: supply.value,
        method: isSubscription ? 'subscription' : 'one-time',
        variantId: supply.dataset.variantId,
        variantTitle: supply.dataset.label,
        methodTitle,
        price: total,
        available: !supply.disabled
      }
    }));
  }

  #dot() {
    const dot = document.createElement('span');
    dot.textContent = '·';
    return dot;
  }
}

if (!customElements.get('ryedical-offer')) {
  customElements.define('ryedical-offer', RyedicalOffer);
}
